// ============================================================================
// AppShell — the authenticated app frame: a sticky Pulse AppHeader with nav +
// a "New client" action, over a max-width content well on the light canvas.
// The active route drives the highlighted nav link. Renders nested routes via
// <Outlet/>.
//
// This is also where the LIVE-SYNC socket lives: useLiveSync(true) opens
// /api/ws for the whole authenticated session, so any change on another device
// invalidates the relevant React Query caches and the UI updates with no
// reload. The trainer identity comes from the auth context (GET /api/auth/me).
//
// The Pulse AppHeader renders plain <a> links (pure presentational). To keep
// real client-side routing we intercept same-origin link clicks inside the
// header and hand them to the router instead of a full page reload.
// ============================================================================
import type { MouseEvent } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppHeader, Button } from "../components";
import { IconPlus } from "../components/icons";
import { useAuth } from "../auth/AuthContext";
import { useLiveSync } from "../sync/useLiveSync";
import { EmailVerifyBanner } from "./EmailVerifyBanner";
import { LanguageToggle } from "../i18n/LanguageToggle";
import i18n from "../i18n/config";
import enAppShell from "../i18n/locales/en/appShell.json";
import zhAppShell from "../i18n/locales/zh/appShell.json";

// Register the appShell namespace on the already-initialized i18n instance.
// Keeping this self-contained here (rather than in the shared config) means the
// shell owns its own copy: nav links, the New-client / Log-out actions and the
// footer brand line all read from `appShell`, falling back to `common` for any
// shared key. `deep: false` + `overwrite: true` keeps it idempotent under HMR.
i18n.addResourceBundle("en", "appShell", enAppShell, false, true);
i18n.addResourceBundle("zh", "appShell", zhAppShell, false, true);

export function AppShell() {
  const { t } = useTranslation("appShell");
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { trainer, logout } = useAuth();

  // Open the live-sync WebSocket for the whole authenticated session.
  useLiveSync(true);

  const NAV = [
    { label: t("nav.overview"), to: "/app" },
    { label: t("nav.clients"), to: "/app/clients" },
    { label: t("nav.calendar"), to: "/app/calendar" },
    { label: t("nav.programs"), to: "/app/templates" },
    { label: t("nav.reports"), to: "/app/reports/business" },
  ];

  // Delegate clicks on the header's internal <a href="/..."> to the router.
  const onHeaderClick = (e: MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement).closest("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href");
    if (!href || !href.startsWith("/")) return; // external / hash → leave alone
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    navigate(href);
  };

  const trainerName = trainer?.display_name ?? trainer?.username ?? "";

  return (
    <div className="min-h-screen">
      <div onClick={onHeaderClick}>
        <AppHeader
          variant="light"
          links={NAV.map((n) => ({
            label: n.label,
            href: n.to,
            active:
              n.to === "/app"
                ? pathname === "/app"
                : pathname.startsWith(n.to),
          }))}
          action={
            <div className="flex items-center gap-2">
              <LanguageToggle />
              <Link to="/app/clients">
                <Button size="sm" iconLeft={<IconPlus width={16} height={16} />}>
                  {t("actions.newClient")}
                </Button>
              </Link>
              <Button size="sm" variant="ghost" onClick={() => void logout()}>
                {t("actions.logOut")}
              </Button>
            </div>
          }
          user={{ name: trainerName }}
        />
      </div>

      <EmailVerifyBanner />

      <main className="mx-auto max-w-shell px-gutter py-8">
        <Outlet />
      </main>

      <footer className="mx-auto max-w-shell px-gutter pb-10 pt-4 text-center text-xs text-body-faint">
        <Link to="/app" className="font-bold text-body-soft hover:text-coral">
          {t("app.name")}
        </Link>{" "}
        · {t("app.tagline")}
      </footer>
    </div>
  );
}
